import HeroSection from '@composites/HeroSection';
import IllustratedList, { CollapseWrapper } from '@composites/IllustratedList';
import VideoSection from '@composites/VideoSection';
import { getEventDetails } from '@store/slices/eventSlice';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { MetaTags } from '@components/MetaTags';
import { getImageSrc } from '@utils/fn';
import { useLocation } from 'react-router-dom';
import MailToButton from '@components/MailToButton';
import { useTheme } from 'styled-components';
import { useMobileWidth } from '@utils/hooks';
import { SPEAKER_EMAIL } from '@utils/constants';

const WorldCafeExplanationForSpeakersPage = () => {
  const { t, i18n } = useTranslation();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const { pathname } = useLocation();
  const theme = useTheme();
  const isMobile = useMobileWidth();
  const slug = pathname.split('/').filter(Boolean).pop() || '';
  const videoItems = [
    {
      id: 'qFwIKXRyMwU',
      link: 'https://www.youtube.com/watch?v=qFwIKXRyMwU',
      title: `we.CONECT - ${t('world-cafe')} ${t('session')}`,
    },
  ];

  const subject = `${t('apply-as-a-speaker-request-subject', { eventName: sluggedEvent?.name })}`;
  const body = `${t('apply-as-a-speaker-request-body', { eventName: sluggedEvent?.name })}`;
  const mailtoLink = `mailto:${SPEAKER_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <>
      <MetaTags
        title={`${sluggedEvent?.name} ${slug?.split('-')?.join(' ')}`}
        description={`${sluggedEvent?.name} ${slug?.split('-')?.join(' ')}`}
        keywords={`${sluggedEvent?.slug}, ${slug?.split('-')?.join(', ')}`}
        image={getImageSrc(sluggedEvent?.design?.eventLogoPosSvg)}
        url={window.location.href}
        twitterCardType="summary"
        type="product"
      />
      <HeroSection
        title={t('world-cafe')}
        description={t('world-cafe-hero-text')}
        darkOverlay
        button={
          <MailToButton
            href={mailtoLink}
            title={t('speakers-title-1')}
            backgroundColor={sluggedEvent?.design?.themeColor}
            hoverBackgroundColor={theme.color.mobilityGrey}
            textColor={theme.color.white}
            borderRadius={theme.borderRadius.radius10}
            fontSize={theme.fontSize.size20}
            lineHeight={theme.lineHeight.height150}
            padding={isMobile ? '1.5rem 2.5rem' : '1.1rem 4.9rem'}
            width="fit-content"
          />
        }
        backgroundImage={getImageSrc(
          'https://media.we-conect.com/images/website/world_cafe_explaination_hero_section.jpg',
          sluggedEvent?.portfolio?.slug
        )}
      />

      <VideoSection
        videoItems={videoItems}
        title={t('world-cafe-video-title')}
        description=""
        buttonText={t('speakers-title-1')}
        buttonURL={mailtoLink}
      />

      <CollapseWrapper sluggedEvent={sluggedEvent}>
        <IllustratedList
          buttonText={t('speakers-title-1')}
          buttonURL={mailtoLink}
          image1={getImageSrc(
            'https://media.we-conect.com/images/website/world_cafe_explaination_1.jpg',
            sluggedEvent?.portfolio?.slug
          )}
          image2={getImageSrc(
            'https://media.we-conect.com/images/website/world_cafe_explaination_2.jpg',
            sluggedEvent?.portfolio?.slug
          )}
          orientation="left"
          sluggedEvent={sluggedEvent}
          listItems={Array.from({ length: 8 }, (_, index) => index + 1).map(
            (index: number) => `how-does-the-world-cafe-work-list-item-${index}`
          )}
          title={t('how-does-the-world-cafe-work')}
        />

        <IllustratedList
          buttonText={t('speakers-title-1')}
          buttonURL={mailtoLink}
          image1={getImageSrc(
            'https://media.we-conect.com/images/website/world_cafe_explaination_3.jpg',
            sluggedEvent?.portfolio?.slug
          )}
          image2={getImageSrc(
            'https://media.we-conect.com/images/website/world_cafe_explaination_4.jpg',
            sluggedEvent?.portfolio?.slug
          )}
          orientation="right"
          sluggedEvent={sluggedEvent}
          listItems={Array.from({ length: 8 }, (_, index) => index + 1).map(
            (index: number) =>
              `how-do-you-prepare-world-cafe-list-item-${index}`
          )}
          title={t('how-do-you-prepare-world-cafe')}
        />
      </CollapseWrapper>
    </>
  );
};

export default WorldCafeExplanationForSpeakersPage;
